/**
 * Geometry and physics for the phone's wheel navigation. Pure functions, no
 * React: the component feeds them pointer angles and animation frames.
 *
 * The wheel is a ring of bubbles centred on the bottom-right "trigger": a
 * circle of which you only see the top-left corner. A thumb sweeps along the
 * arc and the bubbles travel with it, rolling on round the circle and off the
 * screen past either end (not back into the trigger), fading as they go so it
 * is plain there is more to scroll. Angles are in degrees, screen coordinates
 * (y points down): 180° = straight left of the trigger, 270° = straight up.
 */

/** Trigger diameter. */
export const TRIGGER = 64
/** Trigger's distance in from the screen's right edge, and (via DOCK_GAP) up from the bottom: the same
 * both ways, so it sits in from the corner along the 45° diagonal, where a thumb reaches it easily. */
export const GUTTER_X = 44
/** Bubble diameter. */
export const BUBBLE = 58
/** Distance from the trigger's centre to each bubble's centre: far enough out that six bubbles fit on the arc. */
export const RADIUS = 215
/**
 * How far the open arc reaches above the collapsed dock. `--dock-h` already
 * ends at the trigger's top; the highest bubble (straight up, slightly grown)
 * plus its name must sit in this band so a list row is not underneath it.
 */
export const WHEEL_COVER_ABOVE_DOCK = Math.ceil(RADIUS + (BUBBLE * 1.2) / 2 - TRIGGER / 2 + 16)
/** Angle between neighbouring bubbles: 18° at this radius is a ~67px chord, a 9px gap. */
export const STEP_DEG = 18
/** Centre of the visible arc: the diagonal, where the thumb rests naturally. */
export const ARC_DEG = 227
/** Six slots on screen, from just above the trigger's level (182°) to straight up (272°): ARC ± SLOT_DEG. */
export const SLOT_DEG = 45
/** Fully opaque within ARC ± FULL_DEG; fading out beyond, gone by ARC ± EDGE_DEG. */
export const FULL_DEG = 30
export const EDGE_DEG = 75
/** Where the focused bubble sits. Six is even, so it takes the upper of the two middle slots. */
export const FOCUS_DEG = ARC_DEG + STEP_DEG / 2
/** The wheel turns a little faster than the thumb, so a short sweep covers more of the ring. */
export const DRAG_GAIN = 1.25

const RAD = Math.PI / 180

/** Angle of item `i` when the wheel is scrolled to `p` (a position in item units). */
export function itemAngle(i: number, p: number): number {
  return FOCUS_DEG + (i - p) * STEP_DEG
}

/**
 * Opacity on the arc: 1 in the middle, easing down toward the ends so the end bubbles are a
 * little faded (about 0.75: subtle, but it says "more this way"), then gone past the arc.
 */
export function visibility(angle: number): number {
  const d = Math.abs(angle - ARC_DEG)
  if (d <= FULL_DEG) return 1
  if (d >= EDGE_DEG) return 0
  const t = 1 - (d - FULL_DEG) / (EDGE_DEG - FULL_DEG)
  return t * t * (3 - 2 * t)
}

/** Bubble centre relative to the trigger's centre. `out` 0 = tucked at the trigger (closed), 1 = on the ring. */
export function itemOffset(angle: number, out = 1, radius = RADIUS): { x: number; y: number } {
  const r = radius * out
  return { x: Math.cos(angle * RAD) * r, y: Math.sin(angle * RAD) * r }
}

/** Angle of a point around a centre, 0..360. */
export function pointerAngle(px: number, py: number, cx: number, cy: number): number {
  const a = Math.atan2(py - cy, px - cx) / RAD
  return (a + 360) % 360
}

/** Shortest signed turn from angle `from` to angle `to`, in -180..180. */
export function angleDelta(from: number, to: number): number {
  let d = (to - from) % 360
  if (d > 180) d -= 360
  if (d < -180) d += 360
  return d
}

/** Scroll position after the finger turned the wheel by `deltaDeg`: items follow the finger, a little ahead of it. */
export function scrollBy(p: number, deltaDeg: number): number {
  return p - (deltaDeg * DRAG_GAIN) / STEP_DEG
}

/** Nearest valid item index for a scroll position. */
export function nearestIndex(p: number, count: number): number {
  if (count <= 0) return 0
  return Math.min(count - 1, Math.max(0, Math.round(p)))
}

/**
 * Where the wheel is drawn for a raw scroll position: free inside the list,
 * resisted beyond either end (the further you pull, the less it moves) so the
 * ends feel like ends. `over` is the most it can ever stretch, in items.
 */
export function rubberBand(p: number, count: number, over = 0.45): number {
  const max = Math.max(0, count - 1)
  if (p < 0) return -over * (1 - Math.exp(p / over))
  if (p > max) return max + over * (1 - Math.exp(-(p - max) / over))
  return p
}

/** Item the wheel settles on when released at `p` moving `v` items/sec: a flick carries it ~0.3s further. */
export function releaseTarget(p: number, v: number, count: number): number {
  return nearestIndex(p + v * 0.3, count)
}

// Snappy and a little bubbly: very stiff, with damping under critical (zeta ~0.7),
// so a release lands in ~0.2s and pops a few percent of one step past its place.
const STIFFNESS = 520
const DAMPING = 32

export interface SpringState {
  p: number
  v: number
}

/** One semi-implicit Euler step of a spring pulling `p` to `target`. `dt` in seconds. */
export function springStep(s: SpringState, target: number, dt: number): SpringState {
  const step = Math.min(dt, 1 / 30) // a dropped frame must not blow the spring up
  const a = STIFFNESS * (target - s.p) - DAMPING * s.v
  const v = s.v + a * step
  return { p: s.p + v * step, v }
}

/**
 * A spring from 0 to 1 sampled into a CSS `linear()` easing, so a plain CSS transition plays it on
 * the compositor (smooth even when the page's JavaScript is busy). Ends once it is within half a
 * percent of rest, so the tail never drags. `duration` in ms.
 */
export function springEasing(stiffness: number, damping: number, samples = 40): { duration: number; easing: string } {
  const dt = 1 / 240
  const xs = [0]
  let x = 0
  let v = 0
  let t = 0
  while (t < 2) {
    v += (stiffness * (1 - x) - damping * v) * dt
    x += v * dt
    t += dt
    xs.push(x)
    if (Math.abs(1 - x) < 0.005 && Math.abs(v) < 0.1) break
  }
  const pts = Array.from({ length: samples + 1 }, (_, i) => xs[Math.round((i * (xs.length - 1)) / samples)])
  pts[samples] = 1
  return { duration: Math.round(t * 1000), easing: `linear(${pts.map((n) => +n.toFixed(4)).join(', ')})` }
}

/** True once the spring is close enough to rest that it can snap and stop animating. */
export function atRest(s: SpringState, target: number): boolean {
  return Math.abs(s.p - target) < 0.002 && Math.abs(s.v) < 0.02
}

/**
 * Where the wheel opens. Around the page you are on, except that for the
 * everyday pages (the first five bubbles after Create) it opens far enough
 * back that Create, the most-used action, is in view beside your page: you
 * never scroll to reach it. Your page stays marked with its disc either way.
 * Three bubbles sit below the focus, so focusing item 3 still shows item 0.
 */
export function openPosition(activeIndex: number, count: number): number {
  if (activeIndex < 0) return 0
  return nearestIndex(activeIndex <= 5 ? Math.min(activeIndex, 3) : activeIndex, count)
}
